"use client"

import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { MapPin, Globe, ArrowLeft, User } from "lucide-react"

type Doctor = {
  id: string
  name: string
  specialty: string
  qualifications: string
  languages: string[]
  hospitalName: string
  image: string
  docType: string
}

type Location = { id: string; name: string }
type Department = { id: string; name: string }

const toTitleCase = (str: string) =>
  str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())

export default function DoctorsListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [locations, setLocations] = useState<Location[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [selectedLocation, setSelectedLocation] = useState(searchParams.get("location") || "")
  const [selectedDept, setSelectedDept] = useState(searchParams.get("specializationId") || "")

  useEffect(() => {
    fetch("/apis/web/clinics")
      .then(r => r.json())
      .then(data => {
        if (data.result) {
          setLocations(data.result.map((c: any) => ({ id: c.Hospital_Id, name: c.HospitalName })))
        }
      })
      .catch(() => {})

    fetch("/apis/web/specializations")
      .then(r => r.json())
      .then(data => {
        const list: any[] = Array.isArray(data) ? data : data.result || []
        setDepartments(list.filter((s: any) => s.id && s.title).map((s: any) => ({
          id: s.id,
          name: toTitleCase(s.title),
        })))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const location = searchParams.get("location") || ""
    const search = searchParams.get("search") || ""
    const type = searchParams.get("type") || ""
    const doctorid = searchParams.get("doctorid") || ""
    const specializationId = searchParams.get("specializationId") || ""

    setSelectedLocation(location)
    setSelectedDept(specializationId)

    const params = new URLSearchParams()
    if (location) params.append("clnorgcode", location)
    if (doctorid) params.append("doctorId", doctorid)
    if (specializationId) params.append("specializationId", specializationId)

    setLoading(true)
    setError("")

    fetch(`/apis/web/doctorprofiles?${params}`)
      .then(r => r.json())
      .then((data: any) => {
        let list: any[] = data.result || []

        if (type === "DOCTOR" && search) {
          const q = search.trim().toLowerCase()
          list = list.filter((d: any) => (d.DOCTOR || "").toLowerCase().includes(q))
        }

        setDoctors(list.map((d: any) => ({
          id: d.DOCTORID || "",
          name: toTitleCase(d.DOCTOR || ""),
          specialty: toTitleCase(d.Speciality_Name || ""),
          qualifications: d.QUALIFICATION || "",
          languages: d.LANG ? d.LANG.split(",").map((l: string) => l.trim()).filter(Boolean) : [],
          hospitalName: d.HOSPITALNAME || "",
          image: d.IMAGE_URL || "/placeholder.svg?height=300&width=300",
          docType: d.DOCTYPE || "",
        })))
      })
      .catch(() => setError("Failed to load doctors. Please try again."))
      .finally(() => setLoading(false))
  }, [searchParams.toString()])

  const getLocationName = (id: string) =>
    locations.find(l => l.id === id)?.name || id

  const handleLocationChange = (id: string) => {
    setSelectedLocation(id)
    const next = new URLSearchParams(searchParams)
    if (id) next.set("location", id)
    else next.delete("location")
    next.delete("doctorid")
    next.delete("type")
    next.delete("search")
    setSearchParams(next)
  }

  const handleDeptChange = (id: string) => {
    setSelectedDept(id)
    const next = new URLSearchParams(searchParams)
    if (id) {
      next.set("specializationId", id)
      next.set("type", "SPECIALITY")
    } else {
      next.delete("specializationId")
      next.delete("type")
    }
    next.delete("doctorid")
    next.delete("search")
    setSearchParams(next)
  }

  const currentLocation = searchParams.get("location") || ""
  const currentSearch = searchParams.get("search") || ""

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Dropdowns */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Find a Doctor</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {loading ? "Searching..." : `${doctors.length} doctors found`}
                  {currentLocation && ` in ${getLocationName(currentLocation)}`}
                  {currentSearch && ` for "${currentSearch.trim()}"`}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:ml-auto">
              <select
                value={selectedLocation}
                onChange={e => handleLocationChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[160px]"
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              <select
                value={selectedDept}
                onChange={e => handleDeptChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[160px]"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Doctors Grid */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-gray-500">Loading doctors...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        ) : doctors.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Doctors Found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or search criteria.</p>
            <Button onClick={() => {
              setSelectedLocation("")
              setSelectedDept("")
              setSearchParams(new URLSearchParams())
            }}>Clear Filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {doctors.map(doctor => (
              <div key={doctor.id} className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      {doctor.image && doctor.image !== "/placeholder.svg?height=300&width=300" ? (
                        <img
                          src={doctor.image}
                          alt={doctor.name}
                          className="w-full h-full object-cover"
                          onError={e => { (e.target as HTMLImageElement).style.display = "none"; (e.target as HTMLImageElement).parentElement!.querySelector(".fallback-icon")?.classList.remove("hidden") }}
                        />
                      ) : null}
                      <User className={`w-10 h-10 text-gray-400 fallback-icon ${doctor.image && doctor.image !== "/placeholder.svg?height=300&width=300" ? "hidden" : ""}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-base sm:text-lg text-primary truncate">{doctor.name}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm font-medium">{doctor.specialty}</p>
                      {doctor.qualifications && (
                        <p className="text-gray-500 text-xs sm:text-sm mt-1 line-clamp-1">{doctor.qualifications}</p>
                      )}
                      {doctor.languages.length > 0 && (
                        <div className="flex items-center gap-2 mt-2 text-xs sm:text-sm text-gray-500">
                          <Globe className="w-4 h-4 flex-shrink-0" />
                          <span>{doctor.languages.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    {doctor.hospitalName && (
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>{doctor.hospitalName}</span>
                      </div>
                    )}
                    {doctor.docType === "IH" ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          size="sm"
                          className="flex-1 py-2.5 h-auto"
                          onClick={() => navigate(`/hospital-visit?doctorid=${doctor.id}&location=${currentLocation}`)}
                        >
                          Book Now
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-primary text-primary hover:bg-primary hover:text-white py-2.5 h-auto"
                          onClick={() => navigate(`/hospital-visit?doctorid=${doctor.id}&location=${currentLocation}&type=video`)}
                        >
                          Video Consultation
                        </Button>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-700">
                        <p className="font-semibold mb-1">Appointment Call:</p>
                        <p className="text-primary">040 23042777, 040 23043777, 040 35094777</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
